import { encodeFunctionData, type PublicClient } from 'viem'
import { erc20Abi } from 'viem'
import { Principal } from '@dfinity/principal'
import { CkETHMinterCanister } from '@dfinity/cketh'
import { HttpAgent } from '@dfinity/agent'
import { CK_MINTER_CANISTER_ID, SEPOLIA_CK_MINTER_CANISTER_ID } from '../constants/canisterIds'
import minterAbi from '../abi/ckethminter.abi.json'
import getAgent from './getAgent'
import principalToBytes32 from './principalToBytes32'
import { fetchEnv } from './fetchEnv'
import { getEthProvider } from './getEthProvider'
import type { CkErc20Token } from '@dfinity/cketh/dist/candid/minter'
import type { UTXO } from '../interfaces/walletActions'
import { BTCNFT, Chain } from '../interfaces/common'

const USDC_MAINNET_CONTRACT = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48'

export class GasCalculator {
  agent: HttpAgent
  ethProvider: PublicClient
  minterInfo?: {
    ethMinterAddress?: string
    erc20MinterAddress?: string
    supportedErc20Tokens?: CkErc20Token[]
  }

  constructor(chain: Chain) {
    this.agent = getAgent()
    this.ethProvider = getEthProvider(chain)
  }

  async estimateErc20TransferGas(
    fromAddress: string,
    tokenAddress: string,
    transferAmount: number
  ) {
    const { erc20MinterAddress } = await this.getMinterInfo()

    if (!erc20MinterAddress) return

    const isUSDC = tokenAddress.toLowerCase() === USDC_MAINNET_CONTRACT.toLowerCase()

    const data = encodeFunctionData({
      abi: erc20Abi,
      functionName: 'transfer',
      args: [erc20MinterAddress as `0x${string}`, BigInt(transferAmount)],
    })

    const gas = await this.ethProvider.estimateGas({
      account: fromAddress as `0x${string}`,
      to: tokenAddress as `0x${string}`,
      data,
    })

    if (isUSDC) {
      // USDC needs more buffer due to additional checks
      return gas + (gas * BigInt(50)) / BigInt(100) // +50%
    } else {
      // Standard ERC20 tokens need less buffer
      return gas + (gas * BigInt(20)) / BigInt(100) // +20%
    }
  }

  async estimateErc20ApproveGas(
    fromAddress: string,
    tokenAddress: string,
    transferAmount: number,
    contractAddress?: string
  ) {
    const { erc20MinterAddress } = await this.getMinterInfo()

    if (!erc20MinterAddress) return

    const data = encodeFunctionData({
      abi: erc20Abi,
      functionName: 'approve',
      args: [(contractAddress || erc20MinterAddress) as `0x${string}`, BigInt(transferAmount)],
    })

    return this.ethProvider.estimateGas({
      account: fromAddress as `0x${string}`,
      to: tokenAddress as `0x${string}`,
      data,
    })
  }

  async estimateEthDepositGas(principalId: string) {
    const { ethMinterAddress } = await this.getMinterInfo()

    if (!ethMinterAddress) return

    const data = encodeFunctionData({
      abi: minterAbi,
      functionName: 'deposit',
      args: [principalToBytes32(principalId)],
    })

    const estimate = await this.ethProvider.estimateGas({
      to: ethMinterAddress as `0x${string}`,
      data,
    })

    return BigInt(Math.ceil(Number(estimate) * 1.5))
  }

  utxosForAmount(
    utxos: UTXO[],
    userOrdinals: BTCNFT[],
    requiredSatoshis: number,
    selectedOrdinal?: BTCNFT
  ): UTXO[] {
    let utxosToUse: UTXO[] = []

    const ordinalUtxo = selectedOrdinal
      ? utxos.find(
          (utxo) =>
            utxo.mintTxid === selectedOrdinal.txId && utxo.mintIndex === selectedOrdinal.mintIndex
        )
      : undefined

    const filteredUtxos = utxos.filter(
      (utxo) =>
        !userOrdinals.some(
          (ordinal) => utxo.mintTxid === ordinal.txId && utxo.mintIndex === ordinal.mintIndex
        )
    )

    const sortedUtxos = filteredUtxos.sort((a, b) => b.value - a.value)

    for (const utxo of sortedUtxos) {
      if (utxosToUse.reduce((acc, u) => acc + u.value, 0) >= requiredSatoshis) {
        break
      }
      utxosToUse.push(utxo)
    }

    // add ordinal only if sending ordinal
    if (ordinalUtxo) {
      utxosToUse.push(ordinalUtxo)
    }

    return utxosToUse
  }

  private async isSupportedErc20Token(tokenAddress: string) {
    const { supportedErc20Tokens } = await this.getMinterInfo()

    return supportedErc20Tokens?.some((token) => token.erc20_contract_address === tokenAddress)
  }

  private async getMinterInfo() {
    if (!this.minterInfo) {
      const isMainnet = fetchEnv('ETH_CHAIN') !== 'sepolia'
      const cannisterId = isMainnet ? CK_MINTER_CANISTER_ID : SEPOLIA_CK_MINTER_CANISTER_ID

      const minterCanister = CkETHMinterCanister.create({
        canisterId: Principal.from(cannisterId),
        agent: this.agent,
      })

      const minterInfo = await minterCanister.getMinterInfo({ certified: true })

      this.minterInfo = {
        ethMinterAddress: minterInfo.eth_helper_contract_address[0],
        erc20MinterAddress: minterInfo.erc20_helper_contract_address[0],
        supportedErc20Tokens: minterInfo.supported_ckerc20_tokens[0],
      }
    }

    return this.minterInfo!
  }
}
